/**
 * Hold-to-open, as a small machine with the clock handed in, so the decisions —
 * when a press starts, when a moving finger is scrolling instead, when the click
 * after a hold must be swallowed — are tested without a browser.
 *
 * Only a finger or a pen holds: a mouse has a right button for the same menu,
 * and a held left button is the start of a text selection or a drag.
 */

export const LONG_PRESS_MS = 450;
/** How far a finger may wander and still be holding; further is a scroll. As `use-touch-drag`. */
export const LONG_PRESS_SLOP_PX = 10;

export interface PressPoint {
  x: number;
  y: number;
}

export interface PressEvent {
  pointerType: string;
  clientX: number;
  clientY: number;
}

export interface LongPressOptions {
  onLongPress: (point: PressPoint) => void;
  ms?: number;
  slop?: number;
  /** Starts the hold timer; returns its cancel. `setTimeout` in the hook. */
  schedule: (fire: () => void, ms: number) => () => void;
  /** A short buzz as the hold lands, where the device has one. */
  buzz?: () => void;
}

export interface LongPress {
  down(event: PressEvent): void;
  move(event: PressEvent): void;
  /** pointerup, pointercancel (the browser took the gesture for a scroll) and pointerleave. */
  end(): void;
  /** True when this click is the release of a hold and must not also act as a tap. Resets. */
  swallowClick(): boolean;
  /** The native context menu (a right-click, or Android's own long press): opens at the point. */
  contextMenu(event: PressEvent): void;
  /** A hold is timing. */
  readonly pending: boolean;
}

export function createLongPress(options: LongPressOptions): LongPress {
  const ms = options.ms ?? LONG_PRESS_MS;
  const slop = options.slop ?? LONG_PRESS_SLOP_PX;
  let cancel: (() => void) | null = null;
  let origin: PressPoint | null = null;
  let fired = false;

  const clear = () => {
    cancel?.();
    cancel = null;
    origin = null;
  };
  const fire = (point: PressPoint) => {
    // Android sends its own contextmenu at about the moment the timer lands: one menu, not two.
    if (fired) return;
    fired = true;
    options.buzz?.();
    options.onLongPress(point);
  };

  return {
    down(event) {
      clear();
      fired = false;
      if (event.pointerType === "mouse") return;
      const point = { x: event.clientX, y: event.clientY };
      origin = point;
      cancel = options.schedule(() => {
        cancel = null;
        origin = null;
        fire(point);
      }, ms);
    },
    move(event) {
      if (!cancel || !origin) return;
      if (Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > slop) clear();
    },
    end() {
      clear();
    },
    swallowClick() {
      const swallow = fired;
      fired = false;
      return swallow;
    },
    contextMenu(event) {
      clear();
      fire({ x: event.clientX, y: event.clientY });
      // A right-click or the keyboard's menu key is followed by no click, so nothing is
      // left to swallow. Safari's contextmenu is a MouseEvent with no pointerType at all.
      if (event.pointerType !== "touch" && event.pointerType !== "pen") fired = false;
    },
    get pending() {
      return cancel !== null;
    },
  };
}

/* --------------------------------------------------- lift, then drag or menu */

/** Hold this long and the item lifts; moving sooner is a scroll. The board's cards have always lifted at this. */
export const LIFT_MS = 280;

export type LiftPhase = "idle" | "pending" | "lifted" | "dragging";

export interface LiftDragOptions {
  liftMs?: number;
  slop?: number;
  schedule: (fire: () => void, ms: number) => () => void;
  buzz?: () => void;
  /**
   * The lift is the drag (a board's card: it follows the finger at once). Off, a
   * lifted item waits: moving drags it, letting go without moving is `onRelease` —
   * one gesture for both a row's menu and dragging the row, with no second timer.
   */
  dragOnLift?: boolean;
  onLift?: (point: PressPoint) => void;
  onDragStart: (point: PressPoint) => void;
  /** Lifted and let go without moving: open the item's menu at the point. */
  onRelease?: (point: PressPoint) => void;
  onDrop: (point: PressPoint) => void;
  /** A lifted or dragging item was interrupted (touchcancel). */
  onCancel: () => void;
}

export interface LiftDrag {
  down(point: PressPoint): void;
  /** True while the gesture owns the finger, so the caller stops the page scrolling. */
  move(point: PressPoint): boolean;
  /** True when the click this release produces must be swallowed (a lift happened). */
  up(point: PressPoint): boolean;
  cancel(): void;
  readonly phase: LiftPhase;
}

/**
 * Touch drag's gesture, decided without a browser: pending → lifted → dragging,
 * where a finger past the slop before the lift is a scroll and is let go. One
 * machine, so a row that both drags and has a hold menu cannot run two timers
 * that disagree about what the finger meant.
 */
export function createLiftDrag(options: LiftDragOptions): LiftDrag {
  const ms = options.liftMs ?? LIFT_MS;
  const slop = options.slop ?? LONG_PRESS_SLOP_PX;
  let phase: LiftPhase = "idle";
  let origin: PressPoint = { x: 0, y: 0 };
  let last: PressPoint = { x: 0, y: 0 };
  let cancelTimer: (() => void) | null = null;

  const stopTimer = () => {
    cancelTimer?.();
    cancelTimer = null;
  };
  const startDrag = (point: PressPoint) => {
    phase = "dragging";
    options.onDragStart(point);
  };

  return {
    down(point) {
      stopTimer();
      phase = "pending";
      origin = last = point;
      cancelTimer = options.schedule(() => {
        cancelTimer = null;
        if (phase !== "pending") return;
        phase = "lifted";
        options.buzz?.();
        options.onLift?.(last);
        if (options.dragOnLift) startDrag(last);
      }, ms);
    },
    move(point) {
      last = point;
      const far = Math.hypot(point.x - origin.x, point.y - origin.y) > slop;
      if (phase === "pending") {
        if (far) {
          stopTimer();
          phase = "idle";
        }
        return false;
      }
      if (phase === "lifted") {
        if (far) startDrag(point);
        return true;
      }
      return phase === "dragging";
    },
    up(point) {
      stopTimer();
      const was = phase;
      phase = "idle";
      if (was === "dragging") options.onDrop(point);
      else if (was === "lifted") options.onRelease?.(point);
      return was === "lifted" || was === "dragging";
    },
    cancel() {
      stopTimer();
      const was = phase;
      phase = "idle";
      if (was === "lifted" || was === "dragging") options.onCancel();
    },
    get phase() {
      return phase;
    },
  };
}
